// services/lei/leiCountryFlag.ts
// GLEIF static country flag files (e.g. https://search.gleif.org/static/countries/cz.svg).
// Served from a different host than the api.gleif.org data request, so flag loading
// never competes with the main table request for connections / bandwidth.

const COUNTRY_FLAG_BASE = 'https://search.gleif.org/static/countries';

/** code -> resolved state of its flag file. Module level = survives re-renders and table pages. */
const flagState = new Map<string, 'ok' | 'missing'>();
/** codes whose flag request is already in flight - makes sure a country is never requested twice. */
const inFlight = new Set<string>();

/** GLEIF stores the flag files in lower case, the country codes from the API come upper case. */
export const countryFlagUrl = (code: string): string => `${COUNTRY_FLAG_BASE}/${code.trim().toLowerCase()}.svg`;

export type TCountryFlagState = 'ok' | 'missing' | 'unknown';

export const countryFlagState = (code: string): TCountryFlagState => {
    const state = flagState.get(code.trim().toUpperCase());
    return state ?? 'unknown';
};

/**
 * Warms the browser HTTP cache for the given countries during idle time (requestIdleCallback),
 * so the <img> tags in the table render without firing a burst of requests right after the
 * data request resolves. Each country is loaded at most once per session (ok/missing
 * bookkeeping); a missing file (404) is remembered, so it is never requested again.
 */
export const preloadCountryFlags = (codes: string[]) => {
    const todo = Array.from(
        new Set(codes.map(code => code.trim()).filter(code => code && countryFlagState(code) === 'unknown'))
    );
    if (!todo.length) return;

    // only run when the main thread is idle; setTimeout fallback for browsers without rIC.
    // NOTE: checking `window` via an intermediate typed variable on purpose - a direct
    // `'requestIdleCallback' in window` check narrows `window` to `never` in the false
    // branch on TS lib configs that do not declare requestIdleCallback.
    const runWhenIdle = (callback: () => void) => {
        if (typeof window === 'undefined') return;
        const w = window as Window & {
            requestIdleCallback?: (
                cb: () => void,
                options?: {
                    timeout?: number;
                }
            ) => number;
        };
        if (typeof w.requestIdleCallback === 'function') {
            w.requestIdleCallback(() => callback(), {
                timeout: 2000
            });
        } else {
            window.setTimeout(callback, 0);
        }
    };

    runWhenIdle(() => {
        todo.forEach(code => {
            const key = code.toUpperCase();
            if (inFlight.has(key)) return;
            inFlight.add(key);

            const img = new Image();
            img.onload = () => {
                inFlight.delete(key);
                flagState.set(key, 'ok');
            };
            img.onerror = () => {
                inFlight.delete(key);
                flagState.set(key, 'missing');
            };
            img.src = countryFlagUrl(key);
        });
    });
};
